/**
 * Japanese.
 *
 * One plural category for every count, which is why `thread.members` has a
 * single branch here and six in Arabic — and why neither could be written as a
 * ternary in component code.
 *
 * Japanese also has no spaces between words, so any layout that assumed it can
 * break a line at a space will break mid-word here.
 *
 * Needs review by a native speaker before shipping; see the note in `ar.ts`.
 * Politeness level is a real decision in Japanese and this uses plain-polite
 * (です・ます) throughout, which is the usual register for a consumer app.
 */
import type { MessageKey } from './en.ts';

export const ja: Partial<Record<MessageKey, string>> = {
  'app.opening': '金庫を開いています…',
  'app.empty.title': '会話を選択',
  'app.empty.body':
    'メッセージはエンドツーエンドで暗号化されています。サーバーは封をした封筒を受け渡すだけで、中身を開くことはできません。サーバーにアクセスできる人にも開けません。',
  'app.identityAlert.title': 'IDキーが変更されました',
  'app.identityAlert.body':
    '{name} のIDキーが変更されました。アプリを再インストールした可能性がありますが、なりすましの可能性もあります。',
  'app.identityAlert.accept': '新しいキーを受け入れる',
  'app.identityAlert.dismiss': '無視する',

  'onboarding.invited': '{name} さんから招待されています。',
  'onboarding.invited.detail': '登録が終わると、その相手との会話がすでに開いた状態になります。',
  'onboarding.intro':
    'エンドツーエンドで暗号化されたメッセージ。キーはこの端末で生成され、端末の外に出ることはありません。',
  'onboarding.username.label': 'ユーザー名',
  'onboarding.username.hint': '3〜32文字、1つの文字体系で。',
  'onboarding.displayName.label': '表示名',
  'onboarding.phone.label': '電話番号 — 任意',
  'onboarding.phone.hint':
    'すでにあなたの番号を知っている人が見つけられるようにするためだけのものです。アカウント名ではなく、サインインにも使いません。あとで削除しても、ほかには何も失われません。',
  'onboarding.submit': 'アカウントを作成',
  'onboarding.submit.busy': 'キーを生成しています…',
  'onboarding.warning':
    'パスワードも復旧手段もありません。あなたのIDはこのブラウザに保存された秘密鍵です。サイトデータを消去すると、登録し直しになります。',
  'onboarding.username.error.too_short': '3文字以上で入力してください。',
  'onboarding.username.error.too_long': '32文字以内で入力してください。',
  'onboarding.username.error.bad_start': 'ピリオド、ハイフン、アンダースコアで始めることはできません。',
  'onboarding.username.error.disallowed_character': '使用できない文字が含まれています。',
  'onboarding.username.error.mixed_script':
    '2つの文字体系が混在しています。名前を模倣されないよう、1つに統一してください。',
  'onboarding.username.error.reserved': 'この名前は予約されています。',

  'status.online': 'オンライン',
  'status.connecting': '接続中…',
  'status.offline': 'オフライン',
  'sidebar.invite': '友だちを招待',
  'sidebar.findByPhone': '電話番号で検索',
  'sidebar.newChat': '新しい会話',
  'sidebar.search': '会話を検索',
  'sidebar.conversations': '会話',
  'sidebar.empty': 'まだ会話がありません。',
  'sidebar.noMatches': '一致する項目がありません',
  'sidebar.noMessages': 'まだメッセージがありません',
  'sidebar.group': 'グループ',
  'sidebar.notifications.on': '通知はオンです',
  'sidebar.notifications.denied': 'ブラウザで通知がブロックされています',
  'sidebar.notifications.enable': '通知をオンにする',

  'thread.back': '会話一覧に戻る',
  'thread.typing': '{count, plural, other {{names} が入力中…}}',
  'thread.members': '{count, plural, other {メンバー#人}}',
  'thread.verifySafety': '安全番号を確認',
  'thread.encrypted':
    'この会話のメッセージはエンドツーエンドで暗号化されています。サーバーでも読むことはできません。',
  'thread.replyingTo': '返信先: {preview}',
  'thread.attachment': '添付ファイル',
  'thread.cancelReply': '返信をキャンセル',
  'thread.recording': '録音中… 離すと送信',
  'thread.cancelRecording': '録音をキャンセル',
  'thread.composer': 'メッセージ',
  'thread.send': '送信',
  'thread.voice.unavailable.title':
    '音声録音にはHTTPSが必要です。このアドレスではブラウザがマイクをブロックします',
  'thread.voice.unavailable.label': 'HTTPSがないため音声録音は利用できません',
  'thread.voice.start': '音声メッセージを録音',
  'thread.voice.stop': '停止して送信',
  'thread.voice.error.title': '録音できませんでした',

  'bubble.deleted': 'このメッセージは削除されました',
  'bubble.actions': 'メッセージの操作',
  'bubble.reply': '返信',
  'profile.profiles': 'プロフィール',
  'profile.profiles.explain': 'この端末上の別々のアカウント（仕事用、個人用、事業用）。すべて接続されたままなので、どれに届いても通知されます。',
  'profile.profiles.add': 'プロフィールを追加',
  'profile.profiles.create': '作成',
  'profile.profiles.namePlaceholder': '仕事、個人…',
  'profile.profiles.unnamed': '名称未設定のプロフィール',
  'profile.profiles.remove': 'プロフィールを削除',
  'profile.profiles.removeConfirm': 'このアカウントを完全に削除しますか？',
  'profile.profiles.warning': '新しいプロフィールは独自のユーザー名を持つ新しいアカウントで、これの別表示ではありません。復旧もできません。',
  'profile.build': 'ビルド {build}',
  'profile.name.edit': '名前を変更',
  'profile.name.save': '保存',
  'profile.name.cancel': 'キャンセル',
  'profile.name.failed': '名前を変更できませんでした。接続を確認してもう一度お試しください。',
  'profile.delete': 'アカウントを削除',
  'profile.delete.explain': 'アカウントをサーバーから完全に削除します。鍵はこの端末にしかないため、元に戻すことも復旧することもできません。',
  'profile.delete.others': '送信済みのメッセージは受け取った端末に残ります。誰にも通知されず、あなたに書こうとしたときに分かります。',
  'profile.delete.confirmPrompt': '確認のためユーザー名を入力してください',
  'profile.delete.confirm': '完全に削除',
  'profile.delete.failed': 'アカウントを削除できませんでした。接続を確認してもう一度お試しください。',
  'thread.recipientGone': 'この人はもう VAV にいません。メッセージは届きません。',
  'thread.recipientGone.title': 'このアカウントは削除されました',
  'profile.avatar.change': '写真を変更',
  'profile.avatar.remove': '写真を削除',
  'profile.avatar.explain': '写真は暗号化され、会話する相手にのみ送られます。それ以外の人にはイニシャルが表示されます。',
  'avatar.crop.title': '写真を調整',
  'avatar.crop.hint': 'ドラッグで位置を、スライダーでズームを調整。円の中がみんなに見える部分です。',
  'avatar.crop.zoom': 'ズーム',
  'avatar.crop.loadFailed': 'この写真を読み込めませんでした。アプリが対応していない形式かもしれません。別の写真をお試しください。',
  'call.incoming.audio': '音声通話の着信',
  'call.incoming.video': 'ビデオ通話の着信',
  'call.accept': '応答',
  'call.decline': '拒否',
  'call.outgoing': '呼び出し中…',
  'call.cancel': 'キャンセル',
  'call.hangup': '通話終了',
  'call.mute': 'ミュート',
  'call.unmute': 'ミュート解除',
  'call.cameraOff': 'カメラをオフ',
  'call.cameraOn': 'カメラをオン',
  'call.connecting': '接続中…',
  'call.ended.declined': '通話が拒否されました',
  'call.ended.busy': '通話中',
  'call.ended.hangup': '通話が終了しました',
  'call.ended.failed': '通話を開始できませんでした',
  'call.ended.unanswered': '応答なし',
  'thread.call.audio': '音声通話',
  'thread.call.video': 'ビデオ通話',
  'call.legFailed': '接続できませんでした',
  'call.turnMissing': '中継サーバー（TURN）が未設定です。異なるネットワーク間の通話には必要です',
  'call.forProfile': 'プロフィール {name} への着信',
  'call.toSpeaker': 'スピーカー',
  'call.toEarpiece': '受話口',
  'onboarding.link.start': 'アカウントを持っています — この端末をリンク',
  'onboarding.link.title': 'この端末をリンク',
  'onboarding.link.hint': 'アカウントがある端末で「マイ情報」から「新しい端末をリンク」を開き、このコードをスキャンするか貼り付けてください。',
  'onboarding.link.verifyHint': '承認する端末にも同じ数字が表示されていることを確認してください。',
  'onboarding.link.copy': 'リンクコードをコピー',
  'onboarding.link.copied': 'コピーしました ✓',
  'onboarding.link.waiting': '承認を待っています…',
  'onboarding.link.failed': 'リンクが完了しませんでした。両方の端末でやり直してください。',
  'profile.device.remove': '接続解除',
  'profile.logout': 'この端末からログアウト',
  'profile.logout.explain': 'アカウントは他の端末に残ります。この端末に保存されたものはすべて消去されます。',
  'profile.logout.confirm': 'ログアウト',
  'link.approve.stranded': 'このブラウザにはアカウントがないため、新しい端末を承認できません。アカウントがある端末の VAV アプリでこのリンクを開く（またはコードを貼り付ける）でください。',
  'link.approve.title': '新しい端末を承認',
  'link.approve.account': '新しい端末はアカウント {name} に追加されます。',
  'profile.link.start': '新しい端末をリンク',
  'profile.link.hint': '新しい端末に表示されたリンクコードを貼り付けてください。',
  'profile.link.verifyHint': '新しい端末に同じ数字が表示されている場合のみ承認してください。',
  'profile.link.approve': '端末を承認',
  'profile.link.done': '端末をリンクしました',
  'profile.link.failed': '承認できませんでした。コードの期限切れかもしれません。',
  'thread.date.today': '今日',
  'thread.date.yesterday': '昨日',
  'contact.delete': '連絡先を削除',
  'contact.delete.explain': '会話と履歴はこの端末からのみ削除されます。相手に通知はされず、相手のコピーは相手に残ります。',
  'group.leave': 'グループを退出',
  'group.leave.explain': 'メンバーには退出が表示されます。グループとその履歴はこの端末から削除されます。',
  'contact.title': '連絡先の詳細',
  'contact.verified': '確認済み',
  'profile.title': 'アカウント情報',
  'profile.username': 'ユーザー名',
  'profile.thisDevice': 'この端末',
  'profile.devices': '端末',
  'profile.devicesLoading': '読み込み中…',
  'profile.devicesNone': 'サーバーに接続できませんでした。',
  'profile.deviceCurrent': 'これ',
  'profile.install': 'この端末にインストール',
  'profile.install.action': 'ホーム画面に追加',
  'profile.install.already': 'インストール済みのアプリを使用中です。',
  'profile.install.ios': '共有メニューから「ホーム画面に追加」を選んでください。',
  'profile.install.unavailable': 'ブラウザが今インストールを提示していません。多くの場合、すでにインストール済みだからです。アプリ一覧で VAV を探してください。',
  'profile.noRecovery': '鍵はこの端末にのみ存在します。ブラウザのデータを消去したり端末を失うとアカウントは失われ、復旧手段はありません。',
  'bubble.forward': '転送',
  'bubble.select': '選択',
  'bubble.moreReactions': 'その他のリアクション',
  'select.cancel': '選択を解除',
  'select.count': '{count, plural, =0 {メッセージを選択} other {# 件選択中}}',
  'forward.title': '転送先',
  'forward.send': '{count, plural, other {# 件のメッセージ}}を{to, plural, other {# 件のチャット}}に送信',
  'thread.voice.blocked.how': 'Chrome ブラウザ（インストール済みアプリではなく）で vav.chat を開き、アドレスバー左のアイコン → 権限 → 権限をリセット。その後もう一度マイクを押して許可してください。',
  'thread.voice.retry': 'もう一度',
  'thread.voice.blockedByOs': 'このサイトにはマイクの許可があります。つまりブロックはここではなく、ブラウザ自体が端末からマイクの権限を得ていません。',
  'thread.voice.blockedByOs.how': 'Android: 設定 → アプリ → Chrome（インストール済みアプリを使う場合は VAV）→ 権限 → マイク → 許可。',
  'thread.voice.sendFailed': '録音を送信できませんでした。接続を確認してもう一度お試しください。',
  'thread.voice.busy': 'マイクは許可されていますが開けませんでした。別のアプリが使用している可能性があります。',
  'thread.voice.busy.how': 'マイクを使っている可能性のあるもの（通話、音声アシスタント、レコーダー）を終了してからもう一度お試しください。再起動で解放されることもあります。',
  'thread.unreadFrom': '{count, plural, other {新着 # 件}}',
  'thread.voice.blocked': 'このサイトのマイクはブロックされています。拒否された権限は再度要求できません。設定で変更してください。',
  'thread.voice.noMicrophone': 'この端末にマイクが見つかりませんでした。',
  'push.prompt.title': '通知はオフです',
  'push.prompt.body': 'オンにすると、アプリを閉じていてもメッセージの到着を端末が知らせます。',
  'push.prompt.denied': 'このサイトは通知がブロックされています。ブラウザのサイト設定で許可してから戻ってください。',
  'push.prompt.enable': 'オンにする',
  'push.prompt.dismiss': '後で',
  'quote.you': '自分',
  'quote.them': '相手',
  'quote.unavailable': 'メッセージを表示できません',
  'quote.image': '写真',
  'quote.video': '動画',
  'quote.audio': '音声',
  'quote.voice': 'ボイスメッセージ',
  'schedule.title': '後で送信',
  'schedule.explain': 'メッセージは今作成・封印され、選んだ時刻に送信されます。それまでは ⋯ メニューから取り消せます。',
  'schedule.confirm': '予約する',
  'bubble.cancelScheduled': '予約送信を取り消す',
  'bubble.state.scheduled': '予約済み',
  'profile.checkUpdate': '更新を確認',
  'profile.checkUpdate.busy': '確認中…',
  'profile.checkUpdate.current': '最新バージョンです。',
  'bubble.edit': '編集',
  'bubble.edited': '編集済み',
  'thread.editing': 'メッセージを編集中',
  'group.edit.title': 'グループ情報',
  'group.edit.name': 'グループ名',
  'bubble.deleteForMe': '自分だけ削除',
  'bubble.save': '画像を保存',
  'bubble.closeImage': '画像を閉じる',
  'bubble.deleteForEveryone': '全員から削除',
  'bubble.decryptFailed': 'この添付ファイルを復号できませんでした。',
  'bubble.decrypting': '復号しています…',
  'bubble.state.pending': '保留中',
  'bubble.state.sent': '送信済み',
  'bubble.state.delivered': '配信済み',
  'bubble.state.read': '既読',
  'bubble.state.failed': '失敗',

  'attach.label': '添付',
  'attach.camera': 'カメラ',
  'attach.image': '写真',
  'attach.video': '動画',
  'attach.audio': '音声',
  'attach.document': '書類',
  'attach.error.title': '添付できませんでした',

  'media.play': '再生',
  'media.pause': '一時停止',
  'media.seek': '録音内の位置',
  'media.decrypting': '復号しています…',
  'media.permission.library': '写真ライブラリへのアクセス権限がありません',
  'media.permission.camera': 'カメラへのアクセス権限がありません',
  'media.permission.microphone': 'マイクへのアクセス権限がありません',

  'invite.title': '友だちを招待',
  'invite.close': '閉じる',
  'invite.body':
    'リンクを送ってください。開いた相手にはあなたが招待したことが表示され、登録するとすぐに会話が開いた状態になります。',
  'invite.preparing': 'リンクを準備しています…',
  'invite.share': '共有',
  'invite.copy': 'リンクをコピー',
  'invite.copied': 'コピーしました',
  'invite.copyFailed': 'コピーできませんでした。リンクを選択して手動でコピーしてください',
  'invite.caution':
    'リンクは30日間有効で、繰り返し使えます。リンクを持っている人は誰でも登録でき、あなたが招待したことを見られます。公開の場には貼らないでください。',
  'invite.message': '{name} さんがVAVに招待しています — エンドツーエンド暗号化メッセージ。',
  'invite.defaultName': '友だち',

  'newChat.title': '新しい会話',
  'newChat.search': 'ユーザー名、フルネーム、または電話番号',
  'newChat.searchByUsername': 'ユーザー名で検索',
  'newChat.direct': 'チャット',
  'newChat.group': 'グループ',
  'newChat.groupName': 'グループ名',
  'newChat.pickHint': '検索結果から参加者を選んでください。人数に制限はありません。',
  'newChat.searching': '検索しています…',
  'newChat.noResults': 'その名前のユーザーは見つかりませんでした。',
  'newChat.createGroup': 'グループを作成',
  'newChat.openChat': '会話を開く',

  'phone.title': '電話番号',
  'phone.privacy':
    '電話番号は送信前にこの端末でハッシュ化されます。サーバーが保存するのはフィンガープリントだけで、番号そのものは保存しません。',
  'phone.mine': '自分の番号 — 見つけてもらうため',
  'phone.save': '保存',
  'phone.saved': '保存しました',
  'phone.findFriends': '友だちを探す',
  'phone.fromContacts': '連絡先から選ぶ',
  'phone.noContactPicker':
    'このブラウザでは連絡先にアクセスできません。番号を入力して確認するか、AndroidのChromeをお使いください。',
  'phone.checkOne': '番号を1つ確認',
  'phone.search': '検索',
  'phone.noneRegistered': 'これらの番号はいずれも登録されていません。',
  'phone.invalid': '電話番号ではないようです',

  'safety.title': '安全番号',
  'safety.compare':
    'この番号を、別の手段で {name} さんと照合してください。直接会うか電話でどうぞ。番号が一致していれば、間に誰もいません。',
  'safety.confirm': 'この番号を確認しました',
  'safety.changeNote':
    '{name} さんがアプリを再インストールすると番号が変わり、通知が届きます。予期しない変更があったときは、送信を続ける前に確認する価値があります。',
  'safety.noSession':
    '{name} さんとはまだキーを交換していません。メッセージを1通送ってから、ここに戻ってください。',

  'common.cancel': 'キャンセル',
  'push.channel': 'メッセージ',
  'push.newMessage': '新しいメッセージ',
  'language.title': '言語',
  'language.note': 'ブラウザから判定しています。判定が違う場合はここで変更してください。',
  'language.restart': 'レイアウトの方向を切り替えるにはアプリを再起動してください。',
  'app.failed.title': '起動できませんでした',
  'app.failed.retry': 'もう一度試す',
  'phone.invalidHint': '国番号から始まる完全な国際形式でお試しください。',
  'phone.country': '国',
  'phone.national': '国内でかけるときの番号',
  'phone.willSave': '{number} として保存されます',
  'phone.searchCountry': '国を検索',
  'phone.importContacts': '連絡先から探す',
  'phone.importing': '連絡先を確認しています…',
  'phone.importingProgress': '連絡先を確認しています… {total} 件中 {done} 件',
  'phone.importScanning': '連絡先を読み込んでいます…',
  'phone.importScanned': '{entries} 件の項目、確認する番号は {unique, plural, other {# 件}}',
  'phone.contactsDenied': '連絡先を読み取る許可が拒否されました。番号を手入力して確認することはできます。',
  'phone.matched': '{count, plural, =0 {連絡先の方はまだ誰もいません} other {連絡先のうち#人がいます}}',
  'phone.contactsPrivacy': '連絡先はこの端末でハッシュ化されます。アドレス帳そのものが送信されることはありません。',
  'invite.share.subject': 'VAVで一緒に',
};
